import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TableWrapper from "../../../../Components/Common/Tables/Tanstack/components/TableWrapper";
import useEventLogColumns from "../Hook/useEventLogColumns";
import EventLogModal from "../Modal/EventLogModal";
import { EVENT_SOURCE, getOrderEventLogs } from "../OrderService";

/**
 * What the filter offers, and which cr_event_log sources each choice asks the API for.
 *
 * The list lives here rather than on the server so a later integration is one more entry plus its
 * source string - the endpoint takes whatever list it is given. "All" is first because it is the
 * default: opening the tab shows the whole trail, and the other two narrow it.
 */
const SOURCE_FILTERS = [
  { key: "ALL", label: "All", sources: [EVENT_SOURCE.SIBRO, EVENT_SOURCE.CKYC] },
  { key: "SIBRO", label: "Sibro", sources: [EVENT_SOURCE.SIBRO] },
  { key: "CKYC", label: "CKYC", sources: [EVENT_SOURCE.CKYC] },
];

/**
 * The event trail for this order - every call this platform made to Sibro and to CKYC on its
 * behalf, including the ones logged against the order's policies, newest first.
 *
 * Read-only and fetched on its own rather than through the order slice: nothing else on the page
 * needs this data, and it is a plain audit view with no edit path back into the order.
 *
 * Narrowing refetches rather than filtering the rows already held: the source list is what the API
 * takes, so the filter is one parameter rather than a second copy of the same rule on the client.
 */
const EventLogs = () => {
  const selectedOrder = useSelector(
    (state) => state.order.selectedOrders.detailsData
  );
  const orderId = selectedOrder?.entityid;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKey, setFilterKey] = useState("ALL");
  // Which row's payload the modal is showing, and which of its two sides.
  const [viewing, setViewing] = useState(null);

  // The effect depends on the key, not on the array: a fresh array on every render would refetch on
  // every render.
  const sources = useMemo(
    () =>
      (SOURCE_FILTERS.find((filter) => filter.key === filterKey) ??
        SOURCE_FILTERS[0]).sources,
    [filterKey]
  );

  useEffect(() => {
    if (!orderId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    // A slow answer for an order the user has already navigated away from - or for a filter they
    // have already changed - must not overwrite the rows of the one they are looking at now.
    let cancelled = false;
    setLoading(true);

    getOrderEventLogs(orderId, sources).then((data) => {
      if (cancelled) return;
      setEvents(data);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [orderId, sources]);

  const { eventColumns } = useEventLogColumns((event, mode) =>
    setViewing({ event, mode })
  );

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Event source">
          {SOURCE_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`btn ${
                filterKey === filter.key ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setFilterKey(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="drill-table-mapping">
        {loading ? (
          <p>Loading...</p>
        ) : events.length > 0 ? (
          <TableWrapper
            columns={eventColumns}
            data={events}
            totalRows={events.length}
            hidePagination
            hideTabsWrapper={true}
          />
        ) : (
          // Not necessarily an error: orders placed before event logging went live have no rows,
          // and neither do orders that never reached the call the filter is asking about.
          <p>No activity recorded for this order</p>
        )}
      </div>

      <EventLogModal
        show={!!viewing}
        onHide={() => setViewing(null)}
        event={viewing?.event}
        mode={viewing?.mode}
      />
    </div>
  );
};

export default EventLogs;
